import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Menu } from '@ofself/shell';
import {
    Plus, FileUp, History, Bookmark, Lock, MoreHorizontal,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useSavedViews } from '../../contexts/SavedViewsContext';
import { useToast } from '../../components/common';
import NucleusPage, { Verb } from '../../components/nucleus/NucleusPage';
import { Cell } from '../../components/nucleus/Collection';
import { Empty, ShowMore } from '../../components/nucleus/Empty';
import { useOpen } from '../../components/nucleus/Inspector';
import { useConfirm, useRunAction } from '../../components/nucleus/actions';
import { usePlace } from '../../nucleus/place';
import { useSpaceAccess } from '../../nucleus/access';
import NoAccess from '../../components/nucleus/NoAccess';
import {
    PAGE, SORTS, EMPTY_FILTER, filterIsEmpty, sortOf, sortFrom, tintOf, schemaOf, titleOf,
    isSealed, isTheirs, isRatified, loadNodes, loadNode, loadSchemaCounts, upload, ratify, saveNode,
} from '../nucleus/GraphModel';
import { GraphTimeTravelSheet, GraphSaveViewSheet, GraphRenameSheet, GraphNodeEditor } from '../nucleus/GraphSheets';
import NodeInspector from './NodeInspector';
import { RelateSheet } from './NodeSheets';
import FilterRail from './FilterRail';
import { FindBar } from '../../components/nucleus/FacetRail';
import NodesTable from './NodesTable';
import { ByView, DeskTable, ElementTable, ElementCell } from '../../components/nucleus/Shelf';
import { useFacets, useTimeline } from './nodeFacets';
import { readable, createNode, trashNode, readPref, writePref, useNarrow } from './graphData';
import { NodeMark } from '../../components/nucleus/faces';
import { ago, fmt } from '../../nucleus/format';

// NODES — everything the space holds, as an inbox: the FILTER RAIL down the
// left (every facet a histogram: whose, when, kind, app, links, stratum, a
// field's value), the ROWS in the middle (kind mark, title, kind, app, changed,
// made — sortable by their heads), and the open node in the page's inspector
// on the right (NucleusPage's seat, wider here: a node reads wide). ↑/↓ walk
// the rows while a node is open; Esc closes it. Cards remain a View choice.
// Saved views, the graph as it was, upload and New node are the corner's verbs.
// The address carries the sort (?sort=), the layout (?as=cells), one kind
// (?kind=), the saved view (?view=) and the search (?q=), so a link reopens
// the same lens.

const WRAP_PREF = 'nucleus:graph-wrap';
const RAIL_PREF = 'nucleus:graph-rail';
// the open node's seat: wider than other pages' inspectors, remembered apart
const NODE_SEAT = { initial: 480, key: 'nodes' };

const typing = () => {
    const t = document.activeElement;
    return !!t && (/^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName) || t.isContentEditable);
};

export default function NodesPage() {
    const { groupId: group, spaceName, section, page } = usePlace();
    const { encKeys, user } = useAuth();
    const userId = user?.id || user?.user_id;
    // what you may write here: in a group, the realm an admin gave you, scoped
    // to each node (the row's own `can`, which holds what you wrote yourself); a
    // write you may not make is shown off, with why
    const access = useSpaceAccess();
    const canCreate = access.can('create');
    const toast = useToast();
    const confirm = useConfirm();
    const run = useRunAction();
    const saved = useSavedViews();
    const narrow = useNarrow();
    const views = useMemo(() => saved.views || [], [saved.views]);
    const [params, setParams] = useSearchParams();
    const [openId, open, close] = useOpen();

    // ── the lens, in the address ──────────────────────────────────────────
    const setParam = useCallback((patch) => setParams((p) => {
        const n = new URLSearchParams(p);
        Object.entries(patch).forEach(([k, v]) => { if (v == null || v === '') n.delete(k); else n.set(k, String(v)); });
        return n;
    }, { replace: true }), [setParams]);
    const sort = SORTS.some((s) => s.id === params.get('sort')) ? params.get('sort') : 'updated';
    const kind = params.get('kind');                 // a schema id, or 'none' for freeform
    const viewId = params.get('view');
    const query = params.get('q') || '';
    const [find, setFind] = useState(query);
    useEffect(() => {
        const t = setTimeout(() => { if (find.trim() !== query) setParam({ q: find.trim() }); }, 300);
        return () => clearTimeout(t);
    }, [find]); // eslint-disable-line react-hooks/exhaustive-deps

    const [filter, setFilter] = useState(EMPTY_FILTER);
    const [localWrap, setLocalWrap] = useState(() => !!readPref(WRAP_PREF, false));
    const [sheet, setSheet] = useState(null);        // { kind: new|edit|relate|travel|save|rename, … }
    const [rail, setRail] = useState(() => readPref(RAIL_PREF, false)); // the filter rail shown (a phone: over the rows)
    const [railOver, setRailOver] = useState(false);
    const fileRef = useRef(null);

    // ── what is there ─────────────────────────────────────────────────────
    const [data, setData] = useState(null);          // { nodes, total } — null while it loads
    const [error, setError] = useState(null);
    // The schema counts as read, with the space they were read for: on a group
    // switch the last space's kinds are not shown (nor named) while the new
    // space's are read.
    const [schemaRead, setSchemaRead] = useState(null);   // { group, list }
    const schemas = schemaRead && schemaRead.group === group ? schemaRead.list : null;
    const [tick, setTick] = useState(0);
    const [linkTick, setLinkTick] = useState(0);
    const reload = useCallback(() => setTick((t) => t + 1), []);

    const kinds = schemas || [];
    const kindOf = (id) => kinds.find((s) => (s.id || 'none') === id) || null;
    // the kinds picked: one rides the address (?kind=), more ride the filter
    const pickedKinds = kind ? [kind] : filter.schemas.map((k) => (k === '__none__' ? 'none' : k));
    const facetFilter = { ...filter, schemas: pickedKinds.map((k) => (k === 'none' ? '__none__' : k)) };
    const facets = useFacets({ group, filter: facetFilter, search: query, tick, names: schemas });
    const timeline = useTimeline({ group, filter: facetFilter, search: query, tick });
    const scope = kind ? { kind: 'schema', id: kind === 'none' ? null : kind, word: kindOf(kind)?.name || 'Schema' } : { kind: 'all' };

    useEffect(() => { setFilter(EMPTY_FILTER); }, [group]);
    // ONE read of the rows per lens: a new lens starts at the first page; a
    // reload (an edit, a trash, an upload) reads again every row already shown,
    // so the list keeps its length. Show more reads only the page after them.
    const lens = JSON.stringify([group, kind, query, sort, filter]);
    const readLens = useRef(null);
    const shown = useRef(0);
    shown.current = data?.nodes?.length || 0;
    // the rows are being read for a new lens: the last ones stay, dimmed, until
    // the read lands or fails — a search that takes the server 25 s is seen to
    // be under way rather than looking like nothing happened
    const [busy, setBusy] = useState(false);
    useEffect(() => {
        let live = true;
        const limit = readLens.current === lens ? Math.max(PAGE, shown.current) : PAGE;
        readLens.current = lens;
        setBusy(true);
        loadNodes({ group, scope, search: query, sort, filter, limit })
            .then((r) => { if (live) { setData(r); setError(null); } })
            .catch((e) => { if (live) setError(e?.message || 'Your graph didn’t load.'); })
            .finally(() => { if (live) setBusy(false); });
        return () => { live = false; };
    }, [lens, tick]); // eslint-disable-line react-hooks/exhaustive-deps
    const [more, setMore] = useState(false);
    const showMore = () => {
        if (more || !data) return;
        const from = data.nodes.length;
        const at = lens;
        setMore(true);
        loadNodes({ group, scope, search: query, sort, filter, limit: PAGE, offset: from, total: false })
            // appended only while the lens it was read for is still the one shown;
            // a row that moved up a page since (a new node) is not shown twice
            .then((r) => {
                if (readLens.current !== at) return;
                setData((d) => {
                    if (!d) return d;
                    const have = new Set(d.nodes.map((n) => String(n.id)));
                    return { ...d, nodes: [...d.nodes, ...(r.nodes || []).filter((n) => !have.has(String(n.id)))] };
                });
            })
            .catch((e) => toast.error?.(e?.message || 'More nodes didn’t load.'))
            .finally(() => setMore(false));
    };
    useEffect(() => {
        let live = true;
        loadSchemaCounts(group).then((s) => { if (live) setSchemaRead({ group, list: s }); }).catch(() => { if (live) setSchemaRead({ group, list: [] }); });
        return () => { live = false; };
    }, [group, tick]);

    // the open node: off the loaded page, or read by its id through the space's
    // route — at once, without waiting for the list: a deep link (?open=) opens
    // as soon as its own node is read, whichever page it is on
    const nodes = data?.nodes || null;
    const listed = nodes?.find((n) => String(n.id) === String(openId)) || null;
    const [far, setFar] = useState(null);            // the node read by its id, or { missing: id }
    useEffect(() => {
        if (!openId || listed) return undefined;
        let live = true;
        const miss = () => { if (live) setFar({ missing: String(openId) }); };
        loadNode(group, openId).then((n) => { if (!live) return; if (n?.id) setFar(n); else miss(); }).catch(miss);
        return () => { live = false; };
    }, [openId, !!listed, group, tick]); // eslint-disable-line react-hooks/exhaustive-deps
    // gone only when its own read missed it AND the list, once read, doesn't hold it
    const missing = !!openId && far?.missing === String(openId) && !!nodes && !listed;
    useEffect(() => {
        if (!missing) return;
        toast.error?.('That node isn’t in this space, or it’s gone.');
        close();
    }, [missing]); // eslint-disable-line react-hooks/exhaustive-deps
    const current = listed || (far?.id && String(far.id) === String(openId) ? far : null);

    const stage = useRef(null);
    const [stageW, setStageW] = useState(0);
    useEffect(() => {
        const el = stage.current;
        if (!el || typeof ResizeObserver === 'undefined') return undefined;
        const ro = new ResizeObserver(([e]) => setStageW(Math.round(e.contentRect.width)));
        ro.observe(el);
        return () => ro.disconnect();
    }, []);
    useEffect(() => { setRailOver(false); }, [current?.id]);
    // the rail stands beside the rows while there is room; on a phone, or when
    // an open node leaves too little, it folds into the Filters verb and opens over the rows
    const overlay = narrow || (!!current && stageW > 0 && stageW < 1100);
    const railShown = overlay ? railOver : rail;
    const nFilters = [filter.access !== 'owned' && !group, pickedKinds.length, filter.apps.length, filter.rels.length,
        filter.strata.length, filter.after || filter.before, filter.field && filter.value].filter(Boolean).length;

    // ── a node's acts: one array, for its inspector and its right-click ──
    const trashAction = (n) => ({
        id: 'trash', label: 'Trash', danger: true, ...off(access.canOn('delete', n)),
        confirm: {
            title: `Move “${titleOf(n)}” to the trash?`,
            body: `It leaves ${spaceName}’s graph, and its links go with it. You can restore it, links and all, from Trash.`,
            label: 'Move to trash',
        },
        run: async () => {
            await trashNode(group, n.id);
            toast.success?.(`“${titleOf(n)}” is in the trash.`);
            if (String(openId) === String(n.id)) close();
            reload();
        },
    });
    const off = (ok) => (ok ? {} : { disabled: true, hint: access.hint });
    const actionsOf = (n) => {
        if (isTheirs(n, group, userId)) return [];
        const ratified = isRatified(n);
        return [
            {
                id: 'edit', label: 'Edit', primary: true, ...off(access.canOn('edit', n)),
                run: async () => setSheet({ kind: 'edit', node: n, open: await readable(n, encKeys, group).catch(() => n.value_json || {}) }),
            },
            // a link is a node of its own: making one is a create
            { id: 'link', label: 'Link', ...off(canCreate), run: () => setSheet({ kind: 'relate', node: n }) },
            // ratify has no group route: a group's node is the group's, and /nodes/<id>/ratify reaches only yours
            !group && {
                id: 'ratify', label: ratified ? 'Unratify' : 'Ratify',
                run: async () => {
                    const r = await ratify(n.id, !ratified);
                    // unratify goes back to the stratum it had before; when that was never
                    // recorded the server picks inferred, and the toast says why
                    const back = r?.stratum_source === 'before_ratification'
                        ? `“${titleOf(n)}” is unratified: ${r.stratum} again, as it was before you ratified it.`
                        : `“${titleOf(n)}” is unratified. It is ${r?.stratum || 'inferred'} now: what it was before you ratified it isn’t recorded.`;
                    toast.success?.(ratified ? back : `“${titleOf(n)}” is ratified: declared, in your own words.`);
                    reload();
                },
            },
            trashAction(n),
        ].filter(Boolean);
    };

    // ⌘N a new node; Delete trashes the open one (asking first). `/` is the find's.
    useEffect(() => {
        const onKey = (e) => {
            if (document.querySelector('[role="dialog"]')) return;
            if ((e.metaKey || e.ctrlKey) && !e.shiftKey && !e.altKey && e.key.toLowerCase() === 'n') {
                e.preventDefault();
                if (canCreate) setSheet({ kind: 'new' });
                else toast.error?.(access.hint);
                return;
            }
            if (typing() || sheet) return;
            // an inbox: ↑/↓ (or k/j) walk the rows while one is open; Esc puts it away
            if (current && nodes?.length && ['ArrowDown', 'ArrowUp', 'j', 'k'].includes(e.key)) {
                const i = nodes.findIndex((n) => String(n.id) === String(current.id));
                const step = e.key === 'ArrowDown' || e.key === 'j' ? 1 : -1;
                const to = nodes[Math.max(0, Math.min(nodes.length - 1, (i < 0 ? 0 : i + step)))];
                e.preventDefault();
                // at either end it stays put: opening the open node again would close it
                if (to && String(to.id) !== String(current.id)) open(to.id);
                return;
            }
            if (e.key === 'Escape' && current) { e.preventDefault(); close(); return; }
            if ((e.key === 'Delete' || e.key === 'Backspace') && current && !isTheirs(current, group, userId)) {
                e.preventDefault();
                if (access.canOn('delete', current)) run(trashAction(current));
                else toast.error?.(access.hint);
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }); // re-bound each render: it reads the open node and the sheet

    // ── uploads ───────────────────────────────────────────────────────────
    const onUpload = async (e) => {
        const files = [...(e.target.files || [])];
        e.target.value = '';
        if (!files.length) return;
        let ok = 0;
        const failed = [];
        for (const f of files) {
            try { await upload(group, f); ok += 1; } catch (err) { failed.push(`${f.name}: ${err?.message || 'it didn’t go'}`); }
        }
        if (ok) toast.success?.(ok === 1 && files.length === 1 ? `Uploaded ${files[0].name}.` : `Uploaded ${ok} of ${files.length} files.`);
        if (failed.length) toast.error?.(`Couldn’t upload ${failed.length === 1 ? failed[0] : `${failed.length} files. ${failed[0]}`}`);
        reload();
    };

    // ── saved views ───────────────────────────────────────────────────────
    const activeView = views.find((v) => v.id === viewId) || null;
    const wrap = activeView ? !!activeView.settings?.wrapText : localWrap;
    const appliedFor = useRef(null);
    const applyView = useCallback((v) => {
        appliedFor.current = v.id;
        const f = v.filters || {};
        const ids = f.schemas || [];
        const one = ids.length === 1 ? ids[0] : null;
        setFilter({
            ...EMPTY_FILTER,
            access: f.access && f.access !== 'both' ? f.access : f.access === 'both' ? 'all' : 'owned',
            schemas: ids.length > 1 ? ids : [], apps: f.apps || [], rels: f.rels || [], strata: f.strata || [],
            dateField: f.dateField || 'updated_at', after: f.dateAfter || '', before: f.dateBefore || '',
            field: f.valueField || '', value: f.valueValue || '', match: f.valueMatch || 'contains',
        });
        setFind(f.graphSearch || '');
        const s = sortFrom(f.sort, f.order);
        setParam({ view: v.id, kind: one ? (one === '__none__' ? 'none' : one) : null, sort: s === 'updated' ? null : s, q: f.graphSearch || null });
    }, [setParam]);
    // a ?view= link applies its view once the views are read
    useEffect(() => {
        if (!viewId || appliedFor.current === viewId) return;
        const v = views.find((x) => x.id === viewId);
        if (v) applyView(v);
    }, [viewId, views, applyView]);
    const everything = () => {
        appliedFor.current = null;
        setFilter(EMPTY_FILTER);
        setFind('');
        setParam({ view: null, kind: null, q: null });
    };
    const saveView = (name, inside) => {
        const s = sortOf(sort);
        const filters = {
            graphSearch: find.trim(),
            schemas: kind ? [kind === 'none' ? '__none__' : kind] : filter.schemas,
            schemaName: kind ? scope.word : undefined,
            apps: filter.apps, rels: filter.rels, strata: filter.strata, dateField: filter.dateField, dateAfter: filter.after, dateBefore: filter.before,
            valueField: filter.field, valueValue: filter.value, valueMatch: filter.match, access: filter.access, sort: s.field, order: s.order,
        };
        const v = saved.createView(name, filters, inside ? { parentDatabaseId: inside } : undefined);
        saved.clearPending?.();
        if (v?.id) { appliedFor.current = v.id; setParam({ view: v.id }); }
        setSheet(null);
        toast.success?.(`Saved “${name}”.`);
    };
    const setWrap = (on) => {
        if (activeView) saved.updateViewSettings?.(activeView.id, { wrapText: on });
        else { setLocalWrap(on); writePref(WRAP_PREF, on); }
    };
    // forgetting a view forgets the views inside it, one write at a time (the
    // store persists from its own latest list, so a burst would lose all but one)
    const [doomed, setDoomed] = useState([]);
    useEffect(() => {
        const next = doomed.find((id) => views.some((v) => v.id === id));
        if (next) saved.deleteView(next);
        else if (doomed.length) setDoomed([]);
    }, [doomed, views]); // eslint-disable-line react-hooks/exhaustive-deps
    const forget = async (v) => {
        const inner = views.filter((c) => c.settings?.parentDatabaseId === v.id);
        const ok = await confirm({
            title: `Forget “${v.name}”?`,
            body: inner.length
                ? `The view and the ${inner.length === 1 ? 'view' : `${inner.length} views`} inside it go. Your nodes stay as they are.`
                : 'Only the view goes. Your nodes stay as they are.',
            label: 'Forget', danger: true,
        });
        if (!ok) return;
        saved.deleteView(v.id);
        setDoomed(inner.map((c) => c.id));
        if (viewId === v.id) everything();
    };

    // ── the corner ────────────────────────────────────────────────────────
    const parents = views.filter((v) => !v.settings?.parentDatabaseId || !views.some((p) => p.id === v.settings.parentDatabaseId));
    const childrenOf = (v) => views.filter((c) => c.settings?.parentDatabaseId === v.id);
    const viewItems = [
        { label: 'Everything', selected: !activeView, onSelect: everything },
        ...parents.flatMap((v) => [
            { label: v.name, selected: viewId === v.id, onSelect: () => applyView(v) },
            ...childrenOf(v).map((c) => ({ label: `↳ ${c.name}`, selected: viewId === c.id, onSelect: () => applyView(c) })),
        ]),
        { separator: true },
        { label: 'Save this view', onSelect: () => setSheet({ kind: 'save', inside: null }) },
        ...(activeView ? [
            { separator: true },
            { header: `“${activeView.name}”` },
            { label: 'Rename', onSelect: () => setSheet({ kind: 'rename', view: activeView }) },
            { label: 'Wrap text', checked: !!activeView.settings?.wrapText, onSelect: () => setWrap(!activeView.settings?.wrapText) },
            ...(!activeView.settings?.parentDatabaseId ? [{ label: 'New view inside', onSelect: () => setSheet({ kind: 'save', inside: activeView.id }) }] : []),
            { label: 'Forget', danger: true, onSelect: () => forget(activeView) },
        ] : []),
    ];
    const sortItems = SORTS.map((s) => ({ label: s.word, selected: sort === s.id, onSelect: () => setParam({ sort: s.id === 'updated' ? null : s.id }) }));
    // the columns are chosen on the table's own head (DataTable); wrapping is the view's
    const wrapItem = { label: 'Wrap text', checked: wrap, onSelect: () => setWrap(!wrap) };
    const glyph = (icon, label) => <button type="button" className="nucleus-verb" aria-label={label}><span className="nucleus-verb__glyph" aria-hidden="true">{icon}</span></button>;

    const filtered = !filterIsEmpty(filter) || !!kind;
    // ONE "View" block holds how the graph is shown: the saved view, its sort,
    // cells or list, and whether long words wrap (the header stays a few blocks)
    const viewMenu = (
        <Menu
            tooltip="How the graph is shown"
            trigger={(
                <button type="button" className={`nucleus-verb${activeView ? ' is-active' : ''}`} aria-label={activeView ? `View: ${activeView.name}` : 'View'}>
                    <span className="nucleus-verb__glyph" aria-hidden="true"><Bookmark /></span>
                    <span className="nucleus-verb__word">{activeView ? activeView.name : 'View'}</span>
                </button>
            )}
            items={[
                { label: activeView ? `Saved view: ${activeView.name}` : 'Saved views', items: viewItems },
                { label: `Sort: ${sortOf(sort).word}`, items: sortItems },
                wrapItem,
            ]}
        />
    );
    // a phone: the secondary verbs fold into one menu, the same acts
    const moreMenu = (
        <Menu
            tooltip="More"
            trigger={glyph(<MoreHorizontal />, 'More')}
            items={[
                { label: activeView ? `View: ${activeView.name}` : 'Views', items: viewItems },
                ...(!group ? [{ label: 'As it was', onSelect: () => setSheet({ kind: 'travel' }) }] : []),
                { label: `Sort: ${sortOf(sort).word}`, items: sortItems },
                wrapItem,
                { separator: true },
                { label: 'Upload files', disabled: !canCreate, hint: canCreate ? undefined : access.hint, onSelect: () => { if (canCreate) fileRef.current?.click(); } },
            ]}
        />
    );

    // ── kinds, picked in the rail ─────────────────────────────────────────
    const pickKinds = (list) => {
        appliedFor.current = null;
        if (list.length === 1) { setFilter((f) => ({ ...f, schemas: [] })); setParam({ kind: list[0], view: null }); return; }
        setFilter((f) => ({ ...f, schemas: list.map((k) => (k === 'none' ? '__none__' : k)) }));
        setParam({ kind: null, view: null });
    };
    const onFilter = (f) => {
        // Clear: every filter, the search with them — not the links switch, which is not a filter
        if (!f) { appliedFor.current = null; setFilter((was) => ({ ...EMPTY_FILTER, links: !!was.links })); setFind(''); setParam({ kind: null, view: null, q: null }); return; }
        setFilter(f);
    };

    // ── the collection ────────────────────────────────────────────────────
    const total = data?.total ?? 0;
    // what is shown, in words: the saved view, else the kinds picked, else everything (narrowed or not)
    const kindNames = pickedKinds.map((k) => kindOf(k)?.name || facets?.kinds?.find((x) => x.key === k)?.name).filter(Boolean);
    const heading = activeView?.name
        || (kindNames.length === 1 ? kindNames[0] : kindNames.length === 2 ? kindNames.join(' and ') : kindNames.length ? `${kindNames.length} schemas` : null)
        || (filtered || query ? 'Narrowed' : 'Everything');
    const empty = query ? `Nothing matches “${query}”.` : kind || filtered ? 'Nothing of this kind here.' : `Nothing in ${spaceName}’s graph yet.`;

    const inspector = current && (
        <NodeInspector
            key={current.id}
            node={current}
            group={group}
            tick={`${tick}:${linkTick}`}
            actions={actionsOf(current)}
            onOpen={open}
            onClose={close}
            onChanged={reload}
        />
    );

    return (
        <NucleusPage
            section={section.label}
            title={page.label}
            count={data ? total : undefined}
            bodyClassName="gx-body"
            inspector={inspector}
            inspectorSeat={NODE_SEAT}
            verbs={<>
                {narrow ? moreMenu : <>
                {viewMenu}
                {/* /graph/snapshot has no group branch: in a group it would show your personal graph */}
                {!group && <Verb icon={<History />} onClick={() => setSheet({ kind: 'travel' })}>As it was</Verb>}
                <Verb icon={<FileUp />} disabled={!canCreate} title={canCreate ? undefined : access.hint} onClick={() => fileRef.current?.click()}>Upload</Verb>
                </>}
                <Verb primary icon={<Plus />} disabled={!canCreate} title={canCreate ? 'New node  ⌘N' : access.hint} onClick={() => setSheet({ kind: 'new' })}>New node</Verb>
            </>}
        >
            <div ref={stage} className={`gx${railShown ? ' has-rail' : ''}${overlay ? ' is-narrow' : ''}${narrow ? ' is-phone' : ''}`}>
                {railShown && (
                    <FilterRail
                        group={group}
                        filter={filter}
                        onFilter={onFilter}
                        search={find}
                        kinds={{ selected: pickedKinds, onChange: pickKinds }}
                        facets={facets}
                        timeline={timeline}
                        onClose={overlay ? () => setRailOver(false) : () => { setRail(false); writePref(RAIL_PREF, false); }}
                        closeWord={overlay ? 'Close' : 'Collapse'}
                    />
                )}
                <div className="gx-main">
                    {/* the list's own column: the search above the nodes */}
                    <div className="gx-list">
                    <FindBar value={find} onChange={setFind} placeholder="Filter nodes" active={nFilters} filters={railShown}
                        onFilters={() => (overlay ? setRailOver((v) => !v) : (setRail(!rail), writePref(RAIL_PREF, !rail)))} />
                    <div className="gx-rows" aria-busy={busy && data ? 'true' : undefined}>
                        {error && <Empty retry={reload}>{error}</Empty>}
                        {/* a member with No access reads nothing here: one line, not an empty graph */}
                        {access.noAccess ? <NoAccess /> : (<>
                        {/* rows or cards: the corner's switch (Shelf.js) */}
                        <ByView
                            rows={(
                            <NodesTable
                                nodes={nodes}
                                total={total}
                                owner={!group && filter.access !== 'owned'}
                                sort={sort}
                                onSort={(id) => setParam({ sort: id === 'updated' ? null : id })}
                                openId={openId}
                                onOpen={(id) => (String(id) === String(openId) ? close() : open(id))}
                                actionsOf={actionsOf}
                                empty={empty}
                                loading={!data && !error}
                                wrap={wrap}
                                group={group}
                                userId={userId}
                            />
                            )}
                            cards={(
                            <DeskTable top={0} between={40}>
                                <ElementTable title={heading} count={data ? total : undefined} empty={data ? empty : undefined}>
                                    {(nodes || []).map((n) => (
                                        <Cell key={n.id} actions={actionsOf(n)}>
                                            <NodeCell node={n} current={String(n.id) === String(openId)} onPress={() => open(n.id)} />
                                        </Cell>
                                    ))}
                                </ElementTable>
                            </DeskTable>
                        )}
                        />
                        {nodes && <ShowMore shown={nodes.length} total={total} label={`Show ${fmt(Math.min(PAGE, total - nodes.length))} more`} onMore={showMore} />}
                        </>)}
                    </div>
                    </div>
                </div>
            </div>

            <input ref={fileRef} type="file" multiple hidden onChange={onUpload} data-testid="graph-upload" />
            {sheet?.kind === 'new' && (
                <GraphNodeEditor
                    node={null}
                    group={group}
                    onSave={async (d) => {
                        const made = await createNode(group, d, encKeys, { groupName: spaceName });
                        if (made?.existed) (toast.info || toast.success)?.(`“${d.title}” already exists in ${spaceName}’s graph, so nothing new was made. Opened it.`);
                        else toast.success?.(`“${d.title}” is in ${spaceName}’s graph.`);
                        setSheet(null);
                        reload();
                        if (made?.id) open(made.id);
                    }}
                    onClose={() => setSheet(null)}
                />
            )}
            {sheet?.kind === 'edit' && (
                <GraphNodeEditor
                    node={{ ...sheet.node, value_json: sheet.open }}
                    group={group}
                    sealedFrom={sheet.node.value_json}
                    onSave={async (d) => {
                        await saveNode(group, sheet.node, d, encKeys, { opened: sheet.open, groupName: spaceName });
                        toast.success?.('Saved.');
                        setSheet(null);
                        reload();
                    }}
                    onClose={() => setSheet(null)}
                />
            )}
            {sheet?.kind === 'relate' && (
                <RelateSheet
                    group={group}
                    node={sheet.node}
                    onClose={() => setSheet(null)}
                    onDone={() => { setSheet(null); toast.success?.('Linked.'); setLinkTick((t) => t + 1); }}
                />
            )}
            {sheet?.kind === 'travel' && <GraphTimeTravelSheet onClose={() => setSheet(null)} />}
            {sheet?.kind === 'save' && <GraphSaveViewSheet views={views} inside={sheet.inside} onSave={saveView} onClose={() => setSheet(null)} />}
            {sheet?.kind === 'rename' && (
                <GraphRenameSheet
                    name={sheet.view.name}
                    onClose={() => setSheet(null)}
                    onSave={(name) => { saved.renameView?.(sheet.view.id, name); setSheet(null); }}
                />
            )}
        </NucleusPage>
    );
}

/** A node as a cell: its kind's square with the schema's icon, the kind above,
 *  when it last changed below, a lock when a field is sealed. */
export function NodeCell({ node, current, dim, onPress }) {
    const schema = schemaOf(node);
    const tint = tintOf(schema);
    const sealed = Object.values(node.value_json || {}).some(isSealed);
    return (
        <ElementCell
            hue={tint}
            tag={schema}
            mark={<NodeMark schema={schema} schemaId={node.schema_id} hue={tint} size={50} />}
            name={titleOf(node)}
            note={ago(node.deleted_at || node.updated_at, { dateAfter: 7 })}
            badge={sealed ? <Lock size={9} strokeWidth={2.2} aria-label="encrypted" /> : undefined}
            current={current}
            dim={dim}
            onPress={onPress}
            title={titleOf(node)}
        />
    );
}
